<div class="vlab" [class.m-comp]="method === 'comp'" [class.m-stat]="method === 'stat'" [class.m-both]="method === 'both'">

  <div class="vl-hd">
    <h2>Valuation Lab <span class="vl-draft">Draft</span></h2>
    <p class="vl-sub">
      How the comparables engine and the statistical model did against all-in auction results, lot by lot.
      <span *ngIf="summary?.basis">Basis: {{ summary.basis }}.</span>
    </p>
  </div>

  <div *ngIf="!isAdmin" class="vl-banner warn">Valuation Lab is for PlateX admins only.</div>

  <ng-container *ngIf="isAdmin">

    <!-- ================= view switch ================= -->
    <div class="seg view-switch" role="tablist" aria-label="Valuation Lab view">
      <button type="button" role="tab" class="pill" [class.on]="view === 'scorecard'" [attr.aria-selected]="view === 'scorecard'" (click)="setView('scorecard')">Scorecard</button>
      <button type="button" role="tab" class="pill" [class.on]="view === 'research'" [attr.aria-selected]="view === 'research'" (click)="setView('research')">
        Research queue<span *ngIf="researchPending !== null"> ({{ researchPending | number }})</span>
      </button>
      <button type="button" role="tab" class="pill" [class.on]="view === 'upcoming'" [attr.aria-selected]="view === 'upcoming'" (click)="setView('upcoming')">
        Upcoming auction<span *ngIf="upcomingPending !== null"> ({{ upcomingPending | number }})</span>
      </button>
    </div>
    <p class="muted xs rq-breakdown" *ngIf="researchCounts && view === 'research'">
      Research queue: {{ (researchCounts.queued || 0) | number }} queued · {{ (researchCounts.owner_explained || 0) | number }} your notes ·
      {{ (researchCounts.research_requested || 0) | number }} sent to research · {{ (researchCounts.researched || 0) | number }} awaiting review ·
      {{ (researchCounts.reviewed || 0) | number }} reviewed<span *ngIf="researchCounts.error"> · {{ researchCounts.error | number }} error</span>
    </p>

    <pla-vlab-upcoming *ngIf="upcomingVisited" [hidden]="view !== 'upcoming'" (pending)="onUpcomingPending($event)"></pla-vlab-upcoming>

    <pla-vlab-research *ngIf="researchVisited" [hidden]="view !== 'research'"
                       (pending)="onResearchPending($event)" (countsChange)="researchCounts = $event"
                       (openLot)="openLotFromResearch($event)"></pla-vlab-research>

    <!-- scorecard stays mounted while the research view is open, so filters / selection survive -->
    <div class="scorecard" [hidden]="view !== 'scorecard'">

    <!-- ================= 1. controls + freshness ================= -->
    <section class="vl-controls" aria-label="Controls">
      <form class="vl-card vl-value" (ngSubmit)="valuePlate()">
        <div class="fld fld-plate">
          <label for="vl-plate">Value a plate</label>
          <input id="vl-plate" name="plate" class="plate-in" [(ngModel)]="plateInput" placeholder="e.g. 11 RKH"
                 autocomplete="off" autocapitalize="characters" spellcheck="false" />
        </div>
        <div class="fld fld-asof">
          <label for="vl-asof">As of</label>
          <input id="vl-asof" name="asOf" type="date" [(ngModel)]="asOf" />
        </div>
        <div class="fld fld-engine">
          <label for="vl-engine">Engine version</label>
          <select id="vl-engine" name="engine" [(ngModel)]="engine">
            <option *ngIf="!engines.length" value="">{{ summaryLoading ? 'Loading…' : 'Live engine' }}</option>
            <option *ngFor="let e of engines; let i = index" [value]="e">{{ e }}{{ i === 0 ? ' (live)' : '' }}</option>
            <option value="__compare" disabled>Compare: v1 vs candidate (coming)</option>
          </select>
        </div>
        <button type="submit" class="btn-primary-vl" [disabled]="!plateInput.trim() || (detailLoading && !selectedKey)">
          {{ detailLoading && !selectedKey ? 'Valuing…' : 'Value' }}
        </button>
      </form>

      <div class="vl-card vl-fresh">
        <div class="card-hd">
          <h3>Sold data freshness</h3>
          <span class="muted sm" *ngIf="summary?.generated_at">checked {{ longDate(summary.generated_at) }}</span>
        </div>

        <div *ngIf="summaryLoading && !summary" class="muted sm">Loading…</div>
        <div *ngIf="summaryError && !summary" class="muted sm">Unavailable</div>

        <!-- phone: one combined alert instead of the card grid -->
        <div class="fresh-phone" *ngIf="summary && isPhone">
          <div *ngFor="let f of staleFreshness" class="fresh-alert">
            <b>{{ f.label }} · {{ f.note || (f.status === 'missing' ? 'no sold data' : 'stale') }}</b>
            <span>Latest stored: {{ f.latest_batch ? f.latest_batch + ' · ' : '' }}{{ shortDate(f.latest_sale_date) || '—' }}</span>
          </div>
          <div *ngIf="!staleFreshness.length && freshness.length" class="muted sm">All houses up to date.</div>
        </div>

        <div class="fresh-grid" *ngIf="summary && !isPhone">
          <div *ngFor="let f of freshness" class="fresh" [class.bad]="f.status !== 'ok'">
            <div class="f-name">{{ f.label || f.house }}</div>
            <div class="f-line">Latest stored: {{ f.latest_batch ? f.latest_batch + ' · ' : '' }}{{ shortDate(f.latest_sale_date) || '—' }}</div>
            <div class="f-note ok" *ngIf="f.status === 'ok'">{{ f.note || 'Up to date' }}</div>
            <div class="f-note bad" *ngIf="f.status === 'stale'">{{ f.note || 'Stale' }}</div>
            <div class="f-note bad" *ngIf="f.status === 'missing'">{{ f.note || 'No sold data' }}</div>
          </div>
          <div class="fresh counts" *ngIf="summary.counts">
            <div class="f-name">Lots awaiting a result</div>
            <div class="f-line">
              {{ summary.counts.awaiting_result ?? 0 }} awaiting · <span [class.warn-t]="summary.counts.result_missing > 0">{{ summary.counts.result_missing ?? 0 }} result missing</span>
            </div>
            <div class="f-line muted">{{ summary.counts.open ?? 0 }} open · {{ summary.counts.unsold ?? 0 }} unsold</div>
          </div>
        </div>
      </div>
    </section>

    <!-- whole-page error state -->
    <div *ngIf="summaryError && !summary" class="vl-banner err" role="alert">
      <div>
        <b>Valuation Lab data unavailable</b>
        <span>{{ summaryError === 'Valuation Lab data unavailable' ? 'The assistant server did not respond.' : summaryError }}</span>
      </div>
      <button type="button" class="btn-ghost" (click)="reload()">Retry</button>
    </div>

    <!-- ================= 2. method switch + source ================= -->
    <div class="vl-switches">
      <div class="seg" role="group" aria-label="Scoring method">
        <span class="seg-l">Scoring</span>
        <button *ngFor="let m of methods" type="button" class="pill" [class.on]="method === m.key" [attr.aria-pressed]="method === m.key"
                (click)="setMethod(m.key)">{{ m.label }}</button>
        <button type="button" class="pill" [class.on]="method === 'both'" [attr.aria-pressed]="method === 'both'" (click)="setMethod('both')">Side by side</button>
      </div>
      <div class="seg" role="group" aria-label="Estimate source">
        <span class="seg-l">Source</span>
        <button type="button" class="pill" [class.on]="source === 'all'" [attr.aria-pressed]="source === 'all'" (click)="setSource('all')">Live + back-test</button>
        <button type="button" class="pill" [class.on]="source === 'live'" [attr.aria-pressed]="source === 'live'" (click)="setSource('live')">Live only</button>
        <button type="button" class="pill" [class.on]="source === 'backtest'" [attr.aria-pressed]="source === 'backtest'" (click)="setSource('backtest')">Back-test only</button>
      </div>
      <div class="notes">
        <p class="muted sm note">Each method is shown as it comes out; neither reshapes the other.</p>
        <p class="muted sm note">The candidate engine covers dateless plates in the back-test; live lots use the live engine.</p>
      </div>
    </div>

    <!-- ================= 3. KPI row ================= -->
    <section class="vl-kpis" aria-label="Accuracy summary" [class.loading]="summaryLoading">
      <div class="kpi">
        <div class="k-l">Lots scored <span class="hide-phone">(all-in, sold)</span></div>
        <div class="k-v" *ngIf="method !== 'both'">{{ kpi(method)?.scored ?? '—' }}</div>
        <div class="k-v multi" *ngIf="method === 'both'">
          <span *ngFor="let m of methods" [ngClass]="'mk-' + m.key"><i>{{ m.short }}</i> {{ kpi(m.key)?.scored ?? '—' }}</span>
        </div>
        <div class="k-s" *ngIf="statK?.excluded_in_training && (method === 'stat' || method === 'both')">stat excludes {{ statK.excluded_in_training }} in training data</div>
        <div class="k-s" *ngIf="method === 'cand' || method === 'both'">cand: {{ candScope }}</div>
      </div>
      <div class="kpi">
        <div class="k-l">Result inside range</div>
        <div class="k-v" *ngIf="method !== 'both'">{{ pct(kpi(method)?.in_range_pct) }}</div>
        <div class="k-v multi" *ngIf="method === 'both'">
          <span *ngFor="let m of methods" [ngClass]="'mk-' + m.key"><i>{{ m.short }}</i> {{ pct(kpi(m.key)?.in_range_pct) }}</span>
        </div>
        <div class="k-s" *ngIf="method === 'stat' || method === 'both'">stat range = 80% interval</div>
      </div>
      <div class="kpi">
        <div class="k-l">Median error</div>
        <div class="k-v" *ngIf="method !== 'both'">{{ pct(kpi(method)?.median_abs_error_pct) }}</div>
        <div class="k-v multi" *ngIf="method === 'both'">
          <span *ngFor="let m of methods" [ngClass]="'mk-' + m.key"><i>{{ m.short }}</i> {{ pct(kpi(m.key)?.median_abs_error_pct) }}</span>
        </div>
        <div class="k-s">
          <div *ngFor="let m of shownMethods">{{ m.short }} bias <span [ngClass]="errClass(kpi(m.key)?.median_error_pct)">{{ errWord(kpi(m.key)?.median_error_pct) }}</span>
            ({{ kpi(m.key)?.under ?? '—' }} under / {{ kpi(m.key)?.over ?? '—' }} over)</div>
        </div>
      </div>
      <div class="kpi">
        <div class="k-l">Within 25%</div>
        <div class="k-v" *ngIf="method !== 'both'">{{ pct(kpi(method)?.within_25pct) }}</div>
        <div class="k-v multi" *ngIf="method === 'both'">
          <span *ngFor="let m of methods" [ngClass]="'mk-' + m.key"><i>{{ m.short }}</i> {{ pct(kpi(m.key)?.within_25pct) }}</span>
        </div>
      </div>
      <div class="kpi">
        <div class="k-l">Closer to the result</div>
        <div *ngIf="!closerPairs.length" class="k-v">—</div>
        <div class="closer" *ngFor="let p of closerPairs">
          <span class="k-v pair"><i>{{ p.a }}</i> {{ p.an | number }} · <i>{{ p.b }}</i> {{ p.bn | number }}</span>
          <span class="k-s">{{ p.tie | number }} tied · {{ p.compared | number }} compared</span>
        </div>
      </div>
    </section>

    <!-- ================= 4. error grid / breakdowns ================= -->
    <section class="vl-card vl-grid" aria-label="Error by segment">
      <div class="card-hd wrap">
        <h3>
          Where each method is wrong — median error by
          {{ breakdown === 'grid' ? 'style × length' : (breakdown === 'number_block' ? 'number block' : breakdown === 'price_band' ? 'price band' : breakdown) }}
          <span class="muted" *ngIf="method === 'both'">(v1 / cand / stat)</span>
        </h3>
        <div class="seg" role="group" aria-label="Breakdown">
          <button *ngFor="let b of breakdownTabs" type="button" class="pill sm" [class.on]="breakdown === b.id"
                  [attr.aria-pressed]="breakdown === b.id" (click)="breakdown = b.id">{{ b.label }}</button>
        </div>
      </div>

      <div *ngIf="summaryLoading && !summary" class="muted sm pad">Loading…</div>
      <div *ngIf="!summaryLoading && !summary" class="muted sm pad">No accuracy data.</div>

      <!-- desktop grid -->
      <div class="grid-scroll" *ngIf="summary && breakdown === 'grid' && !isPhone">
        <div *ngIf="!gridRows.length" class="muted sm pad">No style × length data yet.</div>
        <div class="egrid" *ngIf="gridRows.length" [style.grid-template-columns]="gridColumns" [style.min-width.px]="gridMinWidth">
          <div></div>
          <div *ngFor="let len of gridLengths" class="g-col">{{ len }} chars</div>
          <ng-container *ngFor="let row of gridRows">
            <div class="g-row">{{ row.label }}</div>
            <ng-container *ngFor="let c of row.cells; let j = index">
              <button type="button" class="cell" [class.thin]="cellThin(c)"
                      [class.active]="isSegmentActive(cellFilter(row.style, gridLengths[j]))"
                      [attr.aria-label]="cellAria(row.style, gridLengths[j], c)"
                      [disabled]="!c || !c.n"
                      (click)="setSegment(cellFilter(row.style, gridLengths[j]))">
                <ng-container *ngIf="c && c.n; else emptyCell">
                  <span class="halves" [class.thirds]="shownMethods.length === 3">
                    <span class="half" *ngFor="let m of shownMethods" [style.background]="heat(segErr(c, m.key), cellThin(c))">
                      <span class="ev">{{ errShort(segErr(c, m.key)) }}</span>
                      <span class="em" *ngIf="method === 'both'">{{ m.short }}</span>
                    </span>
                  </span>
                  <span class="cn">n = {{ c.n }}</span>
                </ng-container>
                <ng-template #emptyCell><span class="ev">—</span><span class="cn">n = 0</span></ng-template>
              </button>
            </ng-container>
          </ng-container>
        </div>
      </div>

      <!-- ranked rows: breakdowns everywhere, "worst segments" on a phone -->
      <div *ngIf="summary && (breakdown !== 'grid' || isPhone)" class="ranked">
        <p class="muted sm" *ngIf="isPhone && breakdown === 'grid'">Worst segments first. Tap to filter the stream.</p>
        <div *ngIf="!rankedRows.length" class="muted sm pad">No data for this breakdown yet.</div>
        <button *ngFor="let r of rankedRows" type="button" class="rrow" [class.thin]="r.thin"
                [class.active]="isSegmentActive(r.filter)" (click)="setSegment(r.filter)">
          <span class="r-l">{{ r.label }}</span>
          <span class="r-v">
            <span class="r-e" *ngFor="let m of shownMethods" [style.background]="heat(r[m.key], r.thin)"><i *ngIf="method === 'both'">{{ m.short }}</i> {{ errShort(r[m.key]) }}</span>
            <span class="r-n">n {{ r.n }}</span>
          </span>
        </button>
      </div>

      <div class="legend" *ngIf="summary">
        <span><i class="sw under"></i>Under-estimates (sold above estimate)</span>
        <span><i class="sw over"></i>Over-estimates</span>
        <span><i class="sw thin"></i>Too few lots (n &lt; {{ minN }})</span>
        <span>{{ isPhone ? 'Tap' : 'Click' }} a {{ breakdown === 'grid' && !isPhone ? 'cell' : 'row' }} to filter the auction stream.</span>
      </div>
    </section>

    <!-- ================= 5 + 6. stream and lot detail ================= -->
    <section class="vl-split" aria-label="Auction stream and lot detail">

      <div class="vl-card vl-stream" #streamPanel>
        <div class="card-hd wrap">
          <h3>Auction stream <span class="muted sm" *ngIf="total">{{ total | number }} lots</span></h3>
          <div class="filters">
            <label class="sr" for="vl-house">House</label>
            <select id="vl-house" [(ngModel)]="house" (ngModelChange)="onStreamFilterChange()">
              <option value="">All houses</option>
              <option *ngFor="let h of houseOptions" [value]="h.key">{{ h.label }}</option>
            </select>
            <label class="sr" for="vl-status">Status</label>
            <select id="vl-status" [(ngModel)]="status" (ngModelChange)="onStreamFilterChange()">
              <option value="">All statuses</option>
              <option *ngFor="let s of statuses" [value]="s.id">{{ s.label }}</option>
            </select>
            <label class="sr" for="vl-source">Source</label>
            <select id="vl-source" [ngModel]="source" (ngModelChange)="setSource($event)">
              <option value="all">Live + back-test</option>
              <option value="live">Live (pre-close) only</option>
              <option value="backtest">Back-test (as-of) only</option>
            </select>
            <label class="sr" for="vl-outside">Outside the estimate</label>
            <select id="vl-outside" [(ngModel)]="outside" (ngModelChange)="onStreamFilterChange()">
              <option value="">All lots</option>
              <option value="above">Sold above the range{{ outsideCounts ? ' (' + (outsideCounts.above | number) + ')' : '' }}</option>
              <option value="below">Sold below the range{{ outsideCounts ? ' (' + (outsideCounts.below | number) + ')' : '' }}</option>
              <option value="either">Either{{ outsideCounts ? ' (' + ((outsideCounts.above + outsideCounts.below) | number) + ')' : '' }}</option>
            </select>
            <label class="sr" for="vl-sort">Sort</label>
            <select id="vl-sort" [(ngModel)]="sort" (ngModelChange)="onStreamFilterChange()">
              <option value="closes_desc">Newest first</option>
              <option value="abs_error_desc">Biggest miss first</option>
              <option value="error_asc">Most under-estimated first</option>
              <option value="cand_abs_error_desc">Biggest candidate miss first</option>
            </select>
            <form class="q" (ngSubmit)="onStreamFilterChange()">
              <label class="sr" for="vl-q">Find a plate</label>
              <input id="vl-q" name="q" [(ngModel)]="q" placeholder="Find plate" autocomplete="off" spellcheck="false" />
            </form>
          </div>
        </div>

        <div class="chip-row" *ngIf="segment">
          <span class="fchip">Segment: {{ segment.label }}
            <button type="button" aria-label="Clear segment filter" (click)="clearSegment()">×</button>
          </span>
        </div>

        <div *ngIf="lotsError" class="vl-banner err slim" role="alert">
          <span>{{ lotsError }}</span>
          <button type="button" class="btn-ghost" (click)="loadLots(false)">Retry</button>
        </div>
        <div *ngIf="lotsLoading && !lots.length" class="muted sm pad">Loading lots…</div>
        <div *ngIf="!lotsLoading && !lotsError && !lots.length" class="muted sm pad">No lots match these filters.</div>

        <!-- desktop table -->
        <div class="tbl-scroll" *ngIf="lots.length && !isPhone">
          <table class="stream">
            <thead>
              <tr>
                <th>Plate</th>
                <th>House · closed</th>
                <th>Segment</th>
                <th *ngFor="let m of methods" [class.dim]="isDim(m.key)">{{ m.label }} <span>{{ m.key === 'cand' ? '(back-test)' : '(pre-close)' }}</span></th>
                <th>All-in result</th>
                <th>Error v1 / cand / stat</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let l of lots; trackBy: trackLot" [class.sel]="l.lot_key === selectedKey" [class.unsold]="l.status === 'unsold'">
                <td>
                  <button type="button" class="pchip plate-font" (click)="openLot(l)" [attr.aria-label]="'Open lot ' + l.registration">{{ l.registration }}</button>
                </td>
                <td class="nowrap">{{ l.house_label || l.house }}<span *ngIf="l.closes_at"> · {{ shortDate(l.closes_at) }}</span>
                  <div class="tag" *ngIf="l.source === 'backtest'">back-test</div>
                </td>
                <td class="seg-c">{{ l.segment_label || '—' }}<span class="muted" *ngIf="l.number_block"> · “{{ l.number_block }}”</span></td>
                <td class="num" *ngFor="let m of methods" [class.dim]="isDim(m.key)" [class.training]="inTraining(l, m.key)">
                  <ng-container *ngIf="lotEst(l, m.key) as e; else noEst">
                    {{ range(e.low, e.high, e.mid, true) }}
                    <div class="sub">{{ gbp(e.mid) }}<span *ngIf="lotConfidence(l, m.key)"> · {{ lotConfidence(l, m.key) }}</span><span *ngIf="inTraining(l, m.key)"> · in training data</span></div>
                  </ng-container>
                  <ng-template #noEst><span class="muted" [title]="lotMethodErr(l, m.key) || ''">—</span></ng-template>
                </td>
                <td class="num strong">{{ resultText(l) }}</td>
                <td class="num errs">
                  <ng-container *ngIf="scored(l); else notScored">
                    <ng-container *ngFor="let m of methods; let last = last">
                      <span [class.dim]="isDim(m.key)">
                        <span *ngIf="inTraining(l, m.key)" class="muted">in training</span>
                        <span *ngIf="!inTraining(l, m.key)" [ngClass]="errClass(lotErrPct(l, m.key))" [title]="lotMethodErr(l, m.key) || ''">{{ errWord(lotErrPct(l, m.key)) }}</span>
                      </span><span class="slash" *ngIf="!last"> / </span>
                    </ng-container>
                  </ng-container>
                  <ng-template #notScored><span class="muted">{{ l.status === 'unsold' ? 'not scored' : '—' }}</span></ng-template>
                </td>
                <td><span class="spill" [ngClass]="'st-' + l.status">{{ statusLabel(l.status) }}</span>
                  <span class="opill" *ngIf="l.outside" [ngClass]="'out-' + l.outside">{{ l.outside }} range</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- phone cards -->
        <div class="cards" *ngIf="lots.length && isPhone">
          <button *ngFor="let l of lots; trackBy: trackLot" type="button" class="lcard" [class.sel]="l.lot_key === selectedKey" (click)="openLot(l)">
            <span class="lc-top">
              <span class="pchip plate-font">{{ l.registration }}</span>
              <span class="lc-pills">
                <span class="opill" *ngIf="l.outside" [ngClass]="'out-' + l.outside">{{ l.outside }} range</span>
                <span class="spill" [ngClass]="'st-' + l.status">{{ statusLabel(l.status) }}</span>
              </span>
            </span>
            <span class="lc-meta">{{ l.house_label || l.house }}<span *ngIf="l.closes_at"> · {{ shortDate(l.closes_at) }}</span><span *ngIf="l.segment_label"> · {{ l.segment_label }}</span><span *ngIf="l.source === 'backtest'"> · back-test</span></span>
            <span class="lc-nums">
              <span *ngFor="let m of methods" [class.dim]="isDim(m.key)" [class.training]="inTraining(l, m.key)">
                <i>{{ m.key === 'comp' ? 'Comps v1' : m.key === 'cand' ? 'Candidate' : 'Statistical' }}</i>
                <b [title]="lotMethodErr(l, m.key) || ''">{{ lotEst(l, m.key) ? range(lotEst(l, m.key).low, lotEst(l, m.key).high, lotEst(l, m.key).mid, true) : '—' }}</b>
              </span>
              <span><i>All-in</i><b class="strong">{{ resultText(l) }}</b></span>
            </span>
            <span class="lc-err" *ngIf="scored(l)">
              <span *ngFor="let m of methods; let last = last" [class.dim]="isDim(m.key)">{{ m.short }}
                <span *ngIf="inTraining(l, m.key)" class="muted">in training</span>
                <span *ngIf="!inTraining(l, m.key)" [ngClass]="errClass(lotErrPct(l, m.key))">{{ errWord(lotErrPct(l, m.key)) }}</span><span *ngIf="!last"> · </span>
              </span>
            </span>
            <span class="lc-err muted" *ngIf="!scored(l)">{{ l.status === 'unsold' ? 'not scored' : l.status === 'open' ? 'locked before close' : '—' }}</span>
          </button>
        </div>

        <div class="more" *ngIf="lots.length">
          <span class="muted sm">Showing {{ lots.length | number }} of {{ total | number }}</span>
          <button type="button" class="btn-ghost" *ngIf="canLoadMore" (click)="loadLots(true)">Load more</button>
          <span class="muted sm" *ngIf="lotsLoading && lots.length">Loading…</span>
        </div>
        <p class="muted sm foot">Error = (estimate − all-in result) ÷ result. Unsold lots are listed but never scored.</p>
      </div>

      <!-- ---------------- lot detail ---------------- -->
      <aside class="vl-card vl-detail" aria-label="Lot detail" #detailPanel>
        <div *ngIf="!detail && !detailLoading && !detailError" class="placeholder">
          <b>Lot detail</b>
          <span class="muted sm">{{ isPhone ? 'Tap' : 'Click' }} a plate in the stream, or value a plate above, to see how it was valued.</span>
        </div>
        <div *ngIf="!detail && detailLoading" class="muted sm pad">Valuing…</div>
        <div *ngIf="detailError" class="vl-banner err slim" role="alert"><span>{{ detailError }}</span></div>

        <ng-container *ngIf="detail as d">
          <div class="d-hd">
            <span class="bigplate plate-font">{{ d.plate }}</span>
            <span class="d-house">
              {{ d.houseLine }}
              <span class="spill" *ngIf="d.status" [ngClass]="'st-' + d.status">{{ statusLabel(d.status) }}</span>
              <span class="tag" *ngIf="d.source === 'backtest'">back-test</span>
              <span class="tag" *ngIf="d.mode === 'value'">ad-hoc valuation</span>
            </span>
          </div>
          <div class="muted sm" *ngIf="d.segment">{{ d.segment }}</div>

          <div class="d-cards">
            <div class="dc" [class.dim]="isDim('comp')">
              <div class="dc-l">Comparables v1<span *ngIf="d.comp?.engine"> · {{ d.comp.engine }}</span></div>
              <ng-container *ngIf="d.comp; else dCompErr">
                <div class="dc-v">{{ range(d.comp.low, d.comp.high, d.comp.mid, false) }}</div>
                <div class="dc-s">
                  <span *ngIf="d.comp.mid != null && d.comp.low != null">mid {{ gbp(d.comp.mid) }} · </span>
                  <span *ngIf="d.comp.confidence">{{ d.comp.confidence }} confidence</span>
                  <span *ngIf="d.comp.n_comps != null"> · {{ d.comp.n_comps | number }} comps</span>
                </div>
              </ng-container>
              <ng-template #dCompErr><div class="dc-v muted" [title]="d.compError || ''">—</div><div class="dc-s muted" *ngIf="d.compError">{{ d.compError }}</div></ng-template>
            </div>
            <div class="dc dc-cand" [class.dim]="isDim('cand')">
              <div class="dc-l">Candidate<span *ngIf="d.cand?.engine"> · {{ d.cand.engine }}</span></div>
              <ng-container *ngIf="d.cand; else dCandErr">
                <div class="dc-v">{{ range(d.cand.low, d.cand.high, d.cand.mid, false) }}</div>
                <div class="dc-s">
                  <span *ngIf="d.cand.mid != null">central {{ gbp(d.cand.mid) }}</span>
                  <span *ngIf="d.cand.confidence"> · {{ d.cand.confidence }} confidence</span>
                  <span *ngIf="d.cand.n_comps != null"> · {{ d.cand.n_comps | number }} comps</span>
                </div>
                <div class="dc-b" *ngIf="d.cand.letter_verdict || d.cand.letter_index != null">
                  <span class="verdict" *ngIf="d.cand.letter_verdict" [ngClass]="verdictClass(d.cand.letter_verdict)">{{ letterVerdict(d.cand.letter_verdict) }}</span>
                  <span class="dc-s" *ngIf="d.cand.letter_index != null">letter index ×{{ d.cand.letter_index | number: '1.2-2' }}</span>
                </div>
                <div class="dc-s" *ngIf="d.cand.basis">{{ d.cand.basis }}</div>
              </ng-container>
              <ng-template #dCandErr><div class="dc-v muted" [title]="d.candError || ''">—</div><div class="dc-s muted" *ngIf="d.candError">{{ d.candError }}</div></ng-template>
            </div>
            <div class="dc" [class.dim]="isDim('stat')">
              <div class="dc-l">Statistical model</div>
              <ng-container *ngIf="d.stat; else dStatErr">
                <div class="dc-v">{{ gbp(d.stat.mid) }}</div>
                <div class="dc-s">80%: {{ range(d.stat.low80, d.stat.high80, null, false) }}<span *ngIf="d.stat.confidence"> · {{ d.stat.confidence }}</span></div>
              </ng-container>
              <ng-template #dStatErr><div class="dc-v muted" [title]="d.statError || ''">—</div><div class="dc-s muted" *ngIf="d.statError">{{ d.statError }}</div></ng-template>
            </div>
            <div class="dc">
              <div class="dc-l">All-in result</div>
              <ng-container *ngIf="d.mode === 'lot'; else dNoResult">
                <div class="dc-v">{{ resultText(d) }}</div>
                <ng-container *ngIf="scored(d)">
                  <div class="dc-s strong" [class.dim]="isDim('comp')">v1 <span [ngClass]="errClass(d.compErrPct)">{{ errWord(d.compErrPct) }}</span></div>
                  <div class="dc-s strong" [class.dim]="isDim('cand')">cand <span [ngClass]="errClass(d.candErrPct)" [title]="d.candError || ''">{{ errWord(d.candErrPct) }}</span></div>
                  <div class="dc-s strong" [class.dim]="isDim('stat')">stat <span *ngIf="!d.stat?.in_training" [ngClass]="errClass(d.statErrPct)">{{ errWord(d.statErrPct) }}</span><span *ngIf="d.stat?.in_training" class="muted">in training</span></div>
                </ng-container>
                <div class="dc-s muted" *ngIf="!scored(d)">{{ d.status === 'unsold' ? 'not scored' : 'not scored yet' }}</div>
              </ng-container>
              <ng-template #dNoResult><div class="dc-v muted">—</div><div class="dc-s muted">no result for an ad-hoc valuation</div></ng-template>
            </div>
          </div>
          <div class="muted xs" *ngIf="d.allInBasis">{{ d.allInBasis }}</div>
          <div class="muted xs" *ngIf="d.stat?.in_training">
            This lot sold before the statistical model's training cut-off{{ summary?.stat_training_cutoff ? ' (' + longDate(summary.stat_training_cutoff) + ')' : '' }} — it was in its training data and is kept out of its score.
          </div>

          <div *ngIf="detailLoading" class="muted sm">Loading how it was valued…</div>

          <ng-container *ngIf="!detailLoading">
            <ng-template #engineTabs>
              <div class="etabs" role="group" aria-label="Engine" *ngIf="hasCandDetail(d)">
                <button type="button" class="pill sm" [class.on]="detailEngine === 'cand'" [attr.aria-pressed]="detailEngine === 'cand'" (click)="setDetailEngine('cand')">Candidate</button>
                <button type="button" class="pill sm" [class.on]="detailEngine === 'comp'" [attr.aria-pressed]="detailEngine === 'comp'" (click)="setDetailEngine('comp')">Comparables v1</button>
              </div>
            </ng-template>

            <div class="d-sec" *ngIf="d.explanation.length || d.candExplanation.length">
              <div class="sec-hd">
                <h4>How it was valued</h4>
                <ng-container *ngTemplateOutlet="engineTabs"></ng-container>
              </div>
              <ol class="why steps" *ngIf="detailEngine === 'cand' && hasCandDetail(d)">
                <li *ngFor="let w of d.candExplanation">{{ w }}</li>
                <li class="muted" *ngIf="!d.candExplanation.length">No candidate explanation returned.</li>
              </ol>
              <ul class="why" *ngIf="detailEngine === 'comp' || !hasCandDetail(d)">
                <li *ngFor="let w of d.explanation">{{ w }}</li>
                <li class="muted" *ngIf="!d.explanation.length">No explanation returned.</li>
              </ul>
            </div>

            <div class="d-sec">
              <div class="sec-hd">
                <h4>Influential comparables</h4>
                <ng-container *ngTemplateOutlet="engineTabs"></ng-container>
              </div>

              <ng-container *ngIf="detailEngine === 'cand' && hasCandDetail(d); else v1Comps">
                <span class="muted xs">{{ d.candComps.length }} shown<span *ngIf="d.cand?.n_comps != null"> · {{ d.cand.n_comps | number }} in the candidate base</span></span>
                <div *ngIf="!d.candComps.length" class="muted sm">No candidate comparables returned.</div>
                <div class="cgroup" *ngFor="let g of d.candCompGroups">
                  <h5>{{ g.title }} <span class="muted">· {{ g.rows.length }}</span></h5>
                  <ng-container *ngTemplateOutlet="compRows; context: { $implicit: g.rows }"></ng-container>
                </div>
              </ng-container>
              <ng-template #v1Comps>
                <span class="muted xs" *ngIf="d.comps.length">
                  most similar first · {{ d.comps.length }}<span *ngIf="d.compsTotal != null"> of {{ d.compsTotal | number }}</span> shown
                </span>
                <div *ngIf="!d.comps.length" class="muted sm">No comparables returned.</div>
                <ng-container *ngIf="d.comps.length">
                  <ng-container *ngTemplateOutlet="compRows; context: { $implicit: d.comps }"></ng-container>
                </ng-container>
              </ng-template>

              <ng-template #compRows let-rows>
                <div class="tbl-scroll" *ngIf="!isPhone">
                  <table class="comps">
                    <thead><tr><th>Plate</th><th>All-in</th><th>Sold</th><th>Why chosen</th><th><span class="sr">Feedback</span></th></tr></thead>
                    <tbody>
                      <ng-container *ngFor="let c of rows; trackBy: trackComp">
                        <tr [class.wrong-open]="wrongOpen === c.registration">
                          <td class="nowrap"><span class="cplate plate-font">{{ c.registration }}</span>
                            <span class="sets" *ngIf="c.sets">{{ setsLabel(c.sets) }}</span></td>
                          <td class="num">{{ gbp(c.price_pence) }}</td>
                          <td class="nowrap muted">{{ longDate(c.date) }}</td>
                          <td class="muted">{{ c.why || c.role || '—' }}</td>
                          <td class="nowrap">
                            <ng-container *ngTemplateOutlet="wrongBtn; context: { $implicit: c }"></ng-container>
                          </td>
                        </tr>
                        <tr class="wrong-row" *ngIf="wrongOpen === c.registration">
                          <td colspan="5"><ng-container *ngTemplateOutlet="wrongForm; context: { $implicit: c }"></ng-container></td>
                        </tr>
                      </ng-container>
                    </tbody>
                  </table>
                </div>
                <div class="clist" *ngIf="isPhone">
                  <div class="citem" *ngFor="let c of rows; trackBy: trackComp">
                    <div class="ci-txt">
                      <span class="ci-1"><span class="cplate plate-font">{{ c.registration }}</span> · {{ gbp(c.price_pence) }}
                        <span class="sets" *ngIf="c.sets">{{ setsLabel(c.sets) }}</span></span>
                      <span class="ci-2 muted">{{ longDate(c.date) }}<span *ngIf="c.why"> · {{ c.why }}</span></span>
                    </div>
                    <ng-container *ngTemplateOutlet="wrongBtn; context: { $implicit: c }"></ng-container>
                    <div class="wrong-row-ph" *ngIf="wrongOpen === c.registration">
                      <ng-container *ngTemplateOutlet="wrongForm; context: { $implicit: c }"></ng-container>
                    </div>
                  </div>
                </div>
              </ng-template>

              <!-- "Why is it wrong?" — opened by Wrong comp, one at a time -->
              <ng-template #wrongForm let-c>
                <div class="wrong-form" role="group" [attr.aria-label]="'Why is ' + c.registration + ' a wrong comparable?'">
                  <div class="wf-hd">Why is <b>{{ c.registration }}</b> wrong?</div>
                  <div class="wf-chips" role="radiogroup" aria-label="Reason">
                    <button *ngFor="let r of wrongReasons; let k = index" type="button" role="radio" class="pill sm"
                            [id]="'wrong-reason-' + k" [class.on]="wrongReason === r.id" [attr.aria-checked]="wrongReason === r.id"
                            (click)="setWrongReason(r.id)">{{ r.label }}</button>
                  </div>
                  <label class="sr" for="wrong-note">Context (optional)</label>
                  <textarea id="wrong-note" rows="2" maxlength="1000" [(ngModel)]="wrongNote" (keydown)="onWrongKeydown($event, c)"
                            placeholder="Context (optional) — e.g. WTF reads as a cheeky phrase, like FU 2 vs AU 2"></textarea>
                  <label class="dk" *ngIf="wrongReason === 'meaning_premium'">
                    <input type="checkbox" [(ngModel)]="wrongAlsoMeaning" />
                    <span>Also note what {{ c.registration }} means<span class="muted" *ngIf="!wrongNote.trim()"> (needs context text)</span></span>
                  </label>
                  <div class="wf-actions">
                    <button type="button" class="btn-dark" [disabled]="!wrongReason || wrongSaving" (click)="saveWrong(c)">{{ wrongSaving ? 'Saving…' : 'Save' }}</button>
                    <button type="button" class="btn-ghost" [disabled]="wrongSaving" (click)="cancelWrong()">Cancel</button>
                    <span class="muted xs hide-phone">Ctrl/⌘+Enter saves</span>
                    <span class="err-t xs" *ngIf="wrongError" role="alert">{{ wrongError }}</span>
                  </div>
                </div>
              </ng-template>

              <ng-template #wrongBtn let-c>
                <span class="saved" *ngIf="savedComps[c.registration] === 'saved'" role="status">Saved<span *ngIf="meaningSaved[c.registration]"> · {{ meaningSaved[c.registration] }}</span></span>
                <button *ngIf="savedComps[c.registration] !== 'saved'" type="button" class="btn-ghost sm"
                        [disabled]="savedComps[c.registration] === 'saving' || alreadyFlagged(c.registration)"
                        [attr.aria-expanded]="wrongOpen === c.registration" (click)="wrongComp(c)">
                  {{ savedComps[c.registration] === 'saving' ? 'Saving…' : alreadyFlagged(c.registration) ? 'Flagged' : 'Wrong comp' }}
                </button>
              </ng-template>

              <form class="suggest" (ngSubmit)="suggest()">
                <label for="vl-should">Should be here</label>
                <input id="vl-should" name="shouldBeHere" [(ngModel)]="shouldBeHere" (ngModelChange)="onSuggestInput()"
                       placeholder="Plate that should be a comparable" autocomplete="off" spellcheck="false" />
                <button type="submit" class="btn-dark" [disabled]="!shouldBeHere.trim() || suggestState === 'saving'">
                  {{ suggestState === 'saving' ? 'Saving…' : 'Suggest' }}
                </button>
                <span class="saved" *ngIf="suggestState === 'saved'" role="status">Saved</span>
              </form>
              <div class="err-t xs" *ngIf="feedbackError">{{ feedbackError }}</div>
            </div>

            <div class="d-sec" *ngIf="d.excluded.length">
              <h4>Left out of the cohort</h4>
              <div class="xchips"><span class="xchip" *ngFor="let x of d.excluded">{{ x.n | number }} {{ x.label }}</span></div>
            </div>

            <!-- Comment on this valuation -> Claude reviews and replies -->
            <div class="d-sec comment-box" *ngIf="d.mode === 'lot'">
              <h4>Comment on this valuation</h4>
              <ul class="cmts" *ngIf="d.comments.length">
                <li *ngFor="let c of d.comments">
                  <div class="cm-hd">
                    <span class="cm-st" [class.answered]="c.status === 'answered'">{{ c.status === 'answered' ? 'Answered' : 'Open' }}</span>
                    <span class="muted xs">{{ c.by }}<span *ngIf="c.at"> · {{ dateTime(c.at) }}</span><span *ngIf="c.engine"> · {{ c.engine }}</span></span>
                  </div>
                  <blockquote>“{{ c.comment }}”</blockquote>
                  <div class="cm-topics" *ngIf="c.topics?.length"><span class="xchip" *ngFor="let t of c.topics">{{ topicLabel(t) }}</span></div>
                  <div class="cm-reply" *ngIf="c.reply"><b>Claude:</b> {{ c.reply }}<span class="muted xs" *ngIf="c.replied_at"> · {{ dateTime(c.replied_at) }}</span></div>
                </li>
              </ul>
              <form class="about-form" (ngSubmit)="saveComment()">
                <label for="vl-comment">How should this have been valued?</label>
                <textarea id="vl-comment" name="commentText" rows="3" [(ngModel)]="commentText" (ngModelChange)="commentSaved = false"
                          (keydown)="onCommentKeydown($event)" placeholder="e.g. the base cohort mixes 3-letter suffixes with dateless"></textarea>
                <div class="cm-chips" role="group" aria-label="Topics (optional)">
                  <button type="button" class="pill sm" *ngFor="let t of commentTopics" [class.on]="commentTopicsOn[t.id]"
                          [attr.aria-pressed]="!!commentTopicsOn[t.id]" (click)="toggleTopic(t.id)">{{ t.label }}</button>
                </div>
                <div class="about-row">
                  <span class="muted xs cm-eng">about {{ commentEngine(d) }}</span>
                  <button type="submit" class="btn-dark" [disabled]="!commentText.trim() || commentSaving">{{ commentSaving ? 'Saving…' : 'Save comment' }}</button>
                </div>
                <span class="saved" *ngIf="commentSaved" role="status">Saved — Claude will review it and reply here.</span>
                <span class="err-t xs" *ngIf="commentError" role="alert">{{ commentError }}</span>
              </form>
            </div>

            <!-- About this plate: owner meaning notes -->
            <div class="d-sec about">
              <h4>About {{ d.plate }}</h4>
              <ul class="pnotes" *ngIf="d.plateNotes.length">
                <li *ngFor="let n of d.plateNotes">
                  <blockquote>“{{ n.why }}”</blockquote>
                  <span class="muted xs"><span *ngIf="n.category">{{ noteCategoryLabel(n.category) }} · </span>{{ n.by }}<span *ngIf="n.at"> · {{ dateTime(n.at) }}</span></span>
                </li>
              </ul>
              <form class="about-form" (ngSubmit)="saveAbout()">
                <label for="vl-about">What does this plate mean / why might it sell high?</label>
                <textarea id="vl-about" name="aboutWhy" rows="2" [(ngModel)]="aboutWhy" (ngModelChange)="aboutSaved = false"
                          (keydown)="onAboutKeydown($event)" placeholder="e.g. WTF reads as a cheeky phrase"></textarea>
                <div class="about-row">
                  <label class="sr" for="vl-about-cat">Category</label>
                  <select id="vl-about-cat" name="aboutCategory" [(ngModel)]="aboutCategory">
                    <option value="">Category (optional)</option>
                    <option *ngFor="let c of noteCategories" [value]="c.id">{{ c.label }}</option>
                  </select>
                  <button type="submit" class="btn-dark" [disabled]="!aboutWhy.trim() || aboutSaving">{{ aboutSaving ? 'Saving…' : 'Save' }}</button>
                </div>
                <span class="saved" *ngIf="aboutSaved" role="status">Saved — Claude will structure it and you'll confirm it in the Research queue.</span>
                <span class="err-t xs" *ngIf="aboutError" role="alert">{{ aboutError }}</span>
              </form>
            </div>

            <div class="d-sec" *ngIf="d.feedback.length">
              <h4>Feedback on this {{ d.mode === 'lot' ? 'lot' : 'plate' }}</h4>
              <ul class="fb">
                <li *ngFor="let f of d.feedback">
                  <span class="fb-main">{{ feedbackLine(f) }}
                    <span class="fb-reason" *ngIf="f.reason_category">{{ reasonLabel(f.reason_category) }}</span>
                    <span class="fb-note" *ngIf="f.note">“{{ f.note }}”</span>
                  </span>
                  <span class="muted xs">{{ feedbackMeta(f) }}</span>
                </li>
              </ul>
              <p class="muted xs">Stored for the improvement loop — feedback never changes a valuation directly.</p>
            </div>
          </ng-container>
        </ng-container>
      </aside>
    </section>
    </div>
  </ng-container>
</div>
